import { useEffect, useRef, useState } from "react";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left.js";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2.js";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down.js";
import ChevronUp from "lucide-react/dist/esm/icons/chevron-up.js";
import Clock3 from "lucide-react/dist/esm/icons/clock-3.js";
import MapPin from "lucide-react/dist/esm/icons/map-pin.js";
import Monitor from "lucide-react/dist/esm/icons/monitor.js";
import Navigation from "lucide-react/dist/esm/icons/navigation.js";
import Share2 from "lucide-react/dist/esm/icons/share-2.js";
import "./preview.css";

const MATCHES = [
  {
    id: "nyc-rb", live: true, league: "MLS", code: "NYC–NYRB", label: "New York City FC vs. New York Red Bulls", summary: "Live · 62′", detail: "Live · 62′", area: "Sample market",
    venues: [
      { id: "nyc-1", name: "Northside Sports Bar", distance: "1.2 mi", source: "venue", confirmed: "today", screens: "multiple", x: 28, y: 38 },
      { id: "nyc-2", name: "Union Hall", distance: "2.8 mi", source: "fan", confirmed: "12 minutes ago", screens: "one", x: 67, y: 56 },
      { id: "nyc-3", name: "Market Tavern", distance: "3.4 mi", source: null, confirmed: null, x: 48, y: 74 },
    ],
  },
  {
    id: "ind-lou", league: "USL", code: "IND–LOU", label: "Indy Eleven vs. Louisville City", summary: "Sat · 7:00 PM", detail: "Saturday · 7:00 PM", area: "Sample market",
    venues: [
      { id: "ind-1", name: "Mass Ave Taproom", distance: "0.9 mi", source: "venue", confirmed: "today", screens: "multiple", x: 60, y: 30 },
      { id: "ind-2", name: "Fountain Square Pub", distance: "2.1 mi", source: null, confirmed: null, x: 30, y: 62 },
      { id: "ind-3", name: "Broad Ripple Grill", distance: "4.6 mi", source: null, confirmed: null, x: 72, y: 72 },
    ],
  },
  {
    id: "iu-nd", league: "NCAA", code: "IU–ND", label: "Indiana vs. Notre Dame", summary: "Fri · 7:00 PM", detail: "Friday · 7:00 PM", area: "Sample market",
    venues: [
      { id: "blm-1", name: "Kirkwood Corner Bar", distance: "0.6 mi", source: "fan", confirmed: "35 minutes ago", screens: "one", x: 38, y: 34 },
      { id: "blm-2", name: "Fourth Street Tavern", distance: "1.4 mi", source: "venue", confirmed: "today", screens: "multiple", x: 70, y: 58 },
      { id: "blm-3", name: "Square Grill", distance: "2.2 mi", source: null, confirmed: null, x: 26, y: 70 },
    ],
  },
  {
    id: "ars-tot", league: "EPL", code: "ARS–TOT", label: "Arsenal vs. Tottenham", summary: "Sun · 11:30 AM", detail: "Sunday · 11:30 AM", area: "Sample market",
    venues: [
      { id: "epl-1", name: "The London Room", distance: "0.8 mi", source: "venue", confirmed: "today", screens: "multiple", x: 31, y: 39 },
      { id: "epl-2", name: "North End Pub", distance: "1.9 mi", source: "fan", confirmed: "18 minutes ago", screens: "one", x: 68, y: 51 },
      { id: "epl-3", name: "Station House", distance: "3.1 mi", source: null, confirmed: null, x: 47, y: 73 },
    ],
  },
];

const screenLabel = (value) => value === "one" ? "One screen" : "Multiple screens";

function VenueResult({ venue, open, onToggle, onNotice, confirmedHere, screenChoice, onScreenChoice, onConfirm }) {
  const actionable = Boolean(venue.source);
  const panelId = `detail-${venue.id}`;
  const visibleScreens = confirmedHere?.screens || venue.screens;
  return (
    <article className={`lean-venue-card${actionable ? " lean-venue-confirmed" : " lean-venue-reference"}${open ? " lean-venue-open" : ""}`}>
      <button type="button" className="lean-venue-main" onClick={onToggle} aria-expanded={open} aria-controls={panelId}>
        <span>
          <strong>{venue.name}</strong>
          <small><MapPin size={12} /> {venue.distance}</small>
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {actionable ? (
        <div className="lean-confirmation"><CheckCircle2 size={15} /><span>{confirmedHere ? "You confirmed this just now" : `Confirmed by ${venue.source === "venue" ? "the venue" : "a fan"} ${venue.confirmed}`}</span></div>
      ) : <div className="lean-reference-label">Reference only · not confirmed</div>}
      {visibleScreens && <div className="lean-screen-signal"><Monitor size={14} /><span>{screenLabel(visibleScreens)}</span></div>}
      {open && (
        <div className="lean-venue-detail" id={panelId}>
          <p>{actionable ? "Open directions or send it to your group." : "Reference only. This place has not been confirmed for the selected match."}</p>
          {actionable && <div className="lean-detail-actions">
            <button type="button" onClick={() => onNotice("Directions will open from verified listings.")}><Navigation size={14} /> Directions</button>
            <button type="button" onClick={() => onNotice("Sharing is not active in this preview.")}><Share2 size={14} /> Share</button>
          </div>}
          {actionable && (
            <>
              {!confirmedHere && <fieldset className="lean-screen-report">
                <legend>How many screens are showing it?</legend>
                <div>
                  {[['one', 'One screen'], ['multiple', 'Multiple screens']].map(([value, label]) => (
                    <button key={value} type="button" className={screenChoice === value ? "lean-screen-choice-selected" : ""} aria-pressed={screenChoice === value} onClick={() => onScreenChoice(value)}><Monitor size={14} />{label}</button>
                  ))}
                </div>
              </fieldset>}
              <button type="button" className={`lean-confirm-button${confirmedHere ? " lean-confirm-done" : ""}`} onClick={() => onConfirm(venue.id, screenChoice)} disabled={Boolean(confirmedHere) || !screenChoice}>
                {confirmedHere ? <><CheckCircle2 size={16} /> Thanks for confirming</> : "Confirm the game is on"}
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}

export default function FadiusWatchPreview() {
  const [matchId, setMatchId] = useState(MATCHES[0].id);
  const [openVenue, setOpenVenue] = useState(MATCHES[0].venues[0].id);
  const [confirmedReport, setConfirmedReport] = useState(null);
  const [screenChoices, setScreenChoices] = useState({});
  const [notice, setNotice] = useState("");
  const noticeTimer = useRef(null);
  const match = MATCHES.find((item) => item.id === matchId) || MATCHES[0];
  const venues = match.venues;

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const showNotice = (message) => {
    window.clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = window.setTimeout(() => setNotice(""), 2600);
  };

  const changeMatch = (id) => {
    const next = MATCHES.find((item) => item.id === id) || MATCHES[0];
    setMatchId(next.id);
    setOpenVenue(next.venues[0].id);
    setConfirmedReport(null);
    setScreenChoices({});
    setNotice("");
  };

  return (
    <main className="lean-app-page">
      <section className="lean-phone" aria-label="Fadius soccer Watch preview">
        <header className="lean-app-header">
          <a href="/" className="lean-back-link"><ArrowLeft size={16} /> Site</a>
          <img src="/brand/downloads/fadius-horizontal-lockup-transparent.png?v=20261023" alt="Fadius" />
          <span className="lean-preview-pill">Early preview</span>
        </header>

        <div className="lean-app-content">
          <div className="lean-sport-row"><span>⚽ Soccer</span><small>Illustrative games and venues</small></div>
          <div className="lean-match-picker">
            <span className="lean-match-picker-label" id="match-picker-label">Which match?</span>
            <div className="lean-match-options" role="group" aria-labelledby="match-picker-label">
              {MATCHES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={item.id === matchId}
                  aria-label={`${item.league}: ${item.label}. ${item.summary}`}
                  className={`lean-match-option${item.id === matchId ? " lean-match-option-selected" : ""}`}
                  onClick={() => changeMatch(item.id)}
                >
                  <span className="lean-league-badge" aria-hidden="true">{item.league}</span>
                  <strong>{item.code}</strong>
                  <small>{item.live && <span className="lean-live-dot" aria-hidden="true" />}{item.summary}</small>
                </button>
              ))}
            </div>
          </div>

          <div className="lean-app-heading">
            <h1>Where is {match.code} on?</h1>
            <span>{match.detail} · {match.area}</span>
          </div>

          <div className="lean-map" role="group" aria-label="Illustrative map of nearby watch venues">
            <span className="lean-map-road road-one" /><span className="lean-map-road road-two" />
            <span className="lean-ring lean-ring-one" /><span className="lean-ring lean-ring-two" />
            <span key={`sweep-${matchId}`} className="lean-sweep" aria-hidden="true" />
            <span key={`ping-${matchId}`} className="lean-ping" aria-hidden="true" />
            <span className="lean-user-dot">You</span>
            {venues.map((venue, index) => (
              <button
                key={`${matchId}-${venue.id}`}
                type="button"
                aria-label={`${venue.name}, ${venue.source ? "confirmed" : "reference only"}`}
                aria-pressed={openVenue === venue.id}
                onClick={() => setOpenVenue(venue.id)}
                className={`lean-map-pin${venue.source ? " lean-map-pin-confirmed" : ""}${openVenue === venue.id ? " lean-map-pin-active" : ""}`}
                style={{ left: `${venue.x}%`, top: `${venue.y}%`, animationDelay: `${0.35 + index * 0.18}s` }}
              ><MapPin size={16} /></button>
            ))}
          </div>

          <div className="lean-results-heading"><strong>Nearby places</strong><span>{venues.filter((venue) => venue.source).length} confirmed</span></div>
          <div className="lean-results">
            {venues.map((venue) => <VenueResult key={venue.id} venue={venue} open={openVenue === venue.id} onToggle={() => setOpenVenue(openVenue === venue.id ? null : venue.id)} onNotice={showNotice} confirmedHere={confirmedReport?.venueId === venue.id ? confirmedReport : null} screenChoice={screenChoices[venue.id] || null} onScreenChoice={(screens) => setScreenChoices((choices) => ({ ...choices, [venue.id]: screens }))} onConfirm={(id, screens) => { setConfirmedReport({ venueId: id, screens }); showNotice("Thanks. This is a preview, so nothing was saved."); }} />)}
          </div>
          <p className="lean-app-footnote"><Clock3 size={14} /> Confirmations say who provided them and when. Unconfirmed places stay reference-only.</p>
        </div>
        {notice && <div className="lean-toast" role="status" aria-live="polite">{notice}</div>}
      </section>
    </main>
  );
}
